/**
 * What a watched site's state means on the desk: the pill's tone and words,
 * which state chip a row falls under, the order the table lists rows in, the
 * four counts at the top, and what an operator can do about a site we cannot
 * judge. Pure: no React, no fetch.
 *
 * Split out of `monitoringView.ts`, which keeps the figures, the strip, the
 * expiry levels, the chart's inputs and the engine's health. Imported through
 * `monitoring.ts`, which re-exports this file.
 */
import type { StatusTone } from "../crm/ui";
import type { MonitorRow, MonitorState, MonitorSummary } from "./monitoring";

// ── State ────────────────────────────────────────────────────────────────────

type StateFields = Pick<MonitorRow, "state" | "paused" | "clientActive" | "lastOkAt">;

/**
 * What a row's pill says: the four stored states, plus three readings the
 * stored state alone does not carry.
 *
 *  - `paused` wins over whatever the last check said: a paused site is not
 *    being checked at all.
 *  - `inactive`: the site's client is not active, so the engine does not check
 *    it either (the server counts it with the paused ones). Its last state is
 *    history, and a green "up" on it would claim a watch nobody keeps.
 *  - `neverUp` is a site the engine has never seen working (`lastOkAt` null)
 *    whose last check failed. The engine shows such a site's failing outcome
 *    as its state (so the code under the pill is honest), but it opens no
 *    outage and sends nobody anything until the site answers once
 *    (`crm-monitoring/lib/transition.ts`, "never seen working"). A mistyped
 *    address, an http-only site asked on https, a www variant that does not
 *    exist: red would put each of those in the same column as a customer
 *    losing visitors, and the bell would disagree with the desk.
 */
export type DisplayState = MonitorState | "paused" | "inactive" | "neverUp";

export function displayState(row: StateFields): DisplayState {
  if (row.paused) return "paused";
  if (!row.clientActive) return "inactive";
  if (row.state === "down" && row.lastOkAt === null) return "neverUp";
  return row.state;
}

/** Never seen working, whatever it is doing now: paused or not, it has no uptime to show. */
export function neverSeenWorking(row: StateFields): boolean {
  return row.lastOkAt === null;
}

/** Not checked by the engine: paused, or its client is inactive. */
export function notChecked(row: StateFields): boolean {
  return row.paused || !row.clientActive;
}

/** Down is the only red. Refused is amber because the site may well be fine -
 *  it is our check that is turned away - and so is a site that never answered,
 *  which needs a look at its address. Unknown, paused and inactive make no claim. */
export function stateTone(row: StateFields): StatusTone {
  const shown = displayState(row);
  if (shown === "up") return "ok";
  if (shown === "down") return "lt";
  if (shown === "blocked" || shown === "neverUp") return "wt";
  return "mut";
}

/** The pill's key under `monitoring.state`. */
export function stateKey(row: StateFields): string {
  return `monitoring.state.${displayState(row)}`;
}

/**
 * The 30-day uptime a row may show. A site never seen working has none to
 * speak of, paused or not: nothing it did before its first answer is observed
 * time (the server's rollup counts none), and a figure beside a pill that says
 * it has never worked would only confuse.
 */
export function shownUptime(row: StateFields & Pick<MonitorRow, "uptime30d">): number | null {
  return neverSeenWorking(row) ? null : row.uptime30d;
}

// ── Desk filters and order ───────────────────────────────────────────────────

export const MONITOR_FILTERS = ["all", "down", "other", "up", "paused"] as const;
export type MonitorFilter = (typeof MONITOR_FILTERS)[number];

/** Which chip a row falls under. Blocked, unknown and never seen working share
 *  one: all three mean "we cannot say", and the desk's question is what needs
 *  a look. An inactive client's site sits with the paused ones, as the
 *  server's summary counts it. */
export function rowFilter(row: StateFields): Exclude<MonitorFilter, "all"> {
  const shown = displayState(row);
  if (shown === "paused" || shown === "inactive") return "paused";
  if (shown === "down" || shown === "up") return shown;
  return "other";
}

/** Search across the words an operator would type: the site, its label and the client. */
export function filterMonitors(rows: readonly MonitorRow[], q: string, filter: MonitorFilter): MonitorRow[] {
  const needle = q.trim().toLocaleLowerCase("et");
  return rows.filter((row) => {
    if (filter !== "all" && rowFilter(row) !== filter) return false;
    if (!needle) return true;
    return [row.host, row.url, row.label ?? "", row.clientName].some((s) =>
      s.toLocaleLowerCase("et").includes(needle),
    );
  });
}

export function filterCounts(rows: readonly MonitorRow[]): Record<MonitorFilter, number> {
  const out: Record<MonitorFilter, number> = { all: rows.length, down: 0, other: 0, up: 0, paused: 0 };
  for (const row of rows) out[rowFilter(row)] += 1;
  return out;
}

const RANK: Record<Exclude<MonitorFilter, "all">, number> = { down: 0, other: 1, up: 2, paused: 3 };

/** What needs a look first: down, then "cannot say", then fine, then paused;
 *  inside each, by client and then site, so the order is stable between polls. */
export function sortMonitors(rows: readonly MonitorRow[]): MonitorRow[] {
  return [...rows].sort(
    (a, b) =>
      RANK[rowFilter(a)] - RANK[rowFilter(b)] ||
      a.clientName.localeCompare(b.clientName, "et") ||
      a.host.localeCompare(b.host, "et"),
  );
}

// ── The four tiles ───────────────────────────────────────────────────────────

export interface DeskCounts {
  total: number;
  up: number;
  /** Confirmed outages: the number the bell and the nav badge show. */
  down: number;
  blocked: number;
  unknown: number;
  neverUp: number;
  /** Blocked, unknown and never seen working: the "cannot say" tile. */
  other: number;
  paused: number;
}

/**
 * The desk's tiles, from the server's summary. The server counts a site never
 * seen working apart (`neverUp`) and a stopped one (paused, or its client
 * inactive) as paused, so the "down" tile is exactly what the bell's "sites
 * down" says and nothing here has to guess from the rows.
 */
export function deskCounts(summary: MonitorSummary): DeskCounts {
  return {
    total: summary.total,
    up: summary.up,
    down: summary.down,
    blocked: summary.blocked,
    unknown: summary.unknown,
    neverUp: summary.neverUp,
    other: summary.blocked + summary.unknown + summary.neverUp,
    paused: summary.paused,
  };
}

// ── What the operator can do ─────────────────────────────────────────────────

export type MonitorAdvice =
  | "neverUp"
  | "ourAddress"
  | "ourResolver"
  | "password"
  | "forbidden"
  | "firewall"
  | "certChain";

/**
 * The next step for a site we cannot judge, as a key under `monitoring.advice`,
 * or null when the code under the pill already says everything.
 *
 * Worded by what turned us away, the same split as the engine's "we cannot
 * check this site" e-mail (`crm-monitoring/lib/alertWords.ts`): a password wall
 * or a plain 403 is not "your firewall refuses us", and saying so would send
 * the customer looking for a firewall rule that does not exist. Only a
 * firewall's own evidence (a challenge page, a block page, a 429) asks for
 * WebAdminBot to be allow-listed outright.
 */
export function adviceKey(row: StateFields & Pick<MonitorRow, "lastCode">): MonitorAdvice | null {
  const shown = displayState(row);
  if (shown === "neverUp") return "neverUp";
  if (shown !== "blocked" && shown !== "unknown") return null;
  const code = row.lastCode;
  if (!code) return null;
  if (code === "our_address_blocked") return "ourAddress";
  if (code === "our_resolver") return "ourResolver";
  if (code === "http_401" || code === "http_407") return "password";
  if (code === "http_403") return "forbidden";
  if (code === "challenge" || code === "blocked" || /^http_\d{3}$/.test(code)) return "firewall";
  if (code === "cert_untrusted") return "certChain";
  return null;
}
